import type { ComponentProps, ReactNode } from 'react';
import type { AdvisorProductType } from '../model/types';
import type { V3FilterGroup, V3GroupsOpen } from '../model/v3-filters';
import type { FilterBar } from './filter-bar';
import type { AdvisorPlannerCounts } from './use-advisor-planner';
import { ListFilter, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import {
  HOTEL_PRICE_LEVELS,
  HOTEL_STAR_FILTERS,
  priceLevelRange,
  priceLevelSymbol,
} from '../model/hotel-class';
import { regionName, titleCaseRegionTitle } from '../model/region-layout';
import { advisorProductTypes } from '../model/types';
import {
  activeFilterCount,
  DEFAULT_V3_GROUPS_OPEN,
  filterSummaryLine,
  isBriefMood,
  selectionSummary,
  toggleAllGroups,
} from '../model/v3-filters';
import css from './planner-v3-filters.module.scss';

const segmentCss = pickCss(css, css.segment);
const chipCss = pickCss(css, css.chip);
const toggleCss = pickCss(css, css.toggle);
const searchCss = pickCss(css, css.search);

const TYPE_LABELS: Record<AdvisorProductType, string> = {
  hotels: 'Hotels',
  experiences: 'Experiences',
  restaurants: 'Restaurants',
};

type PlannerV3FiltersProps = {
  filterBar: ComponentProps<typeof FilterBar>;
  counts: AdvisorPlannerCounts;
  search: string;
  onSearchChange: (search: string) => void;
  onClear: () => void;
  /** Moods from the client brief — ringed green while not selected. */
  briefMoods?: readonly string[];
  /** Countries to switch between (the brief's destinations); all when absent. */
  countryChoices?: readonly string[];
  /** Star class and price level, shown on the Hotels list. */
  hotelFilters?: PlannerV3HotelFilters;
};

export type PlannerV3HotelFilters = {
  stars: readonly number[];
  priceLevels: readonly number[];
  onToggleStars: (stars: number) => void;
  onTogglePriceLevel: (level: number) => void;
};

export const PlannerV3Filters = ({
  filterBar,
  counts,
  search,
  onSearchChange,
  onClear,
  briefMoods = [],
  countryChoices,
  hotelFilters,
}: PlannerV3FiltersProps) => {
  const [collapsed, setCollapsed] = useState(false);
  // The search is an icon until opened; it stays open while it holds text.
  const [searchOpen, setSearchOpen] = useState(false);
  const showSearch = searchOpen || search !== '';
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);
  const closeSearch = () => {
    onSearchChange('');
    setSearchOpen(false);
  };
  const [picked, setPicked] = useState<V3GroupsOpen>(DEFAULT_V3_GROUPS_OPEN);
  // Country starts folded when the brief already chose the countries (it may
  // load after the filters), until the agent opens or closes it.
  const [countryTouched, setCountryTouched] = useState(false);
  const open: V3GroupsOpen = countryTouched
    ? picked
    : { ...picked, country: countryChoices === undefined };
  const setOpen = (next: V3GroupsOpen) => {
    setCountryTouched(true);
    setPicked(next);
  };
  const {
    country,
    countries,
    regions,
    moods,
    selectedRegionIds,
    activeRegionId,
    moodRegionIds,
    selectedMoods,
    selectedTags,
    selectedType,
  } = filterBar;
  const countryOptions = countryChoices ?? countries;
  const regionTitle = (id: string) =>
    regionName(regions.find((region) => region.id === id)?.title ?? '');
  const regionLabels = selectedRegionIds.map(regionTitle).filter(Boolean);
  const moodLabels = selectedMoods.map(titleCaseRegionTitle);
  const tagLabels = selectedTags.map(titleCaseRegionTitle);
  const hotel = selectedType === 'hotels' ? hotelFilters : undefined;
  const starLabels = (hotel?.stars ?? []).map((stars) => `${stars}★`);
  const priceLabels = (hotel?.priceLevels ?? []).map(priceLevelSymbol);
  const active = activeFilterCount({
    regions: selectedRegionIds.length,
    moods: selectedMoods.length,
    tags: selectedTags.length,
    hotel: starLabels.length + priceLabels.length,
    search,
  });
  const allOpen = Object.values(open).every(Boolean);

  const group = (
    key: V3FilterGroup,
    label: string,
    summary: string,
    chips: ReactNode,
    extra?: ReactNode
  ) => (
    <div className={css.group} key={key}>
      <button
        aria-expanded={open[key]}
        className={css.groupHead}
        onClick={() => setOpen({ ...open, [key]: !open[key] })}
        type="button"
      >
        <span className={css.groupLabel}>{label}</span>
        {extra}
        {open[key] ? null : <span className={css.groupSummary}>{summary}</span>}
        <span
          aria-hidden="true"
          className={joinCss(css.chevron, open[key] && css.chevronIsAlone)}
        >
          {open[key] ? '▲' : '▼'}
        </span>
      </button>
      {open[key] ? <div className={css.chips}>{chips}</div> : null}
    </div>
  );

  return (
    <section aria-label="Filters" className={css.root}>
      <div className={css.searchRow}>
        {/* One box that grows out of the icon and folds back into it. */}
        <div className={searchCss({ isOpen: showSearch })}>
          <button
            aria-expanded={showSearch}
            aria-label="Search the catalogue"
            className={css.searchIcon}
            onClick={() =>
              showSearch ? searchRef.current?.focus() : setSearchOpen(true)
            }
            tabIndex={showSearch ? -1 : 0}
            type="button"
          >
            <Search aria-hidden="true" size={15} strokeWidth={2.2} />
          </button>
          <input
            aria-hidden={showSearch ? undefined : true}
            aria-label="Search the catalogue"
            className={css.searchInput}
            onBlur={() => {
              if (search === '') setSearchOpen(false);
            }}
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') closeSearch();
            }}
            placeholder="Search hotels, experiences, restaurants…"
            ref={searchRef}
            tabIndex={showSearch ? 0 : -1}
            type="search"
            value={search}
          />
          {showSearch ? (
            <button
              aria-label="Close search"
              className={css.searchClose}
              onClick={closeSearch}
              onMouseDown={(event) => event.preventDefault()}
              type="button"
            >
              <X aria-hidden="true" size={14} strokeWidth={2.2} />
            </button>
          ) : null}
        </div>
        <button
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Show filters' : 'Hide filters'}
          className={toggleCss({ isCollapsed: collapsed })}
          onClick={() => setCollapsed((prev) => !prev)}
          type="button"
        >
          <ListFilter aria-hidden="true" size={14} strokeWidth={2.4} />
          {active > 0 ? <span className={css.badge}>{active}</span> : null}
        </button>
      </div>

      {collapsed ? (
        <button
          className={css.summary}
          onClick={() => setCollapsed(false)}
          type="button"
        >
          <span className={css.summaryText}>
            {filterSummaryLine({
              typeLabel: TYPE_LABELS[selectedType],
              regionLabels,
              moodLabels,
              tagLabels,
              hotelLabels: [...starLabels, ...priceLabels],
              search,
            })}
          </span>
          <span className={css.summaryHint}>· tap to expand</span>
        </button>
      ) : (
        <div className={css.body}>
          {countryOptions.length > 1
            ? group(
                'country',
                'Country',
                country,
                countryOptions.map((option) => (
                  <button
                    aria-pressed={option === country}
                    className={chipCss({
                      tone: option === country ? 'active' : undefined,
                    })}
                    key={option}
                    onClick={() => filterBar.onSelectCountry(option)}
                    type="button"
                  >
                    {option}
                  </button>
                ))
              )
            : null}

          {group(
            'region',
            'Region',
            selectionSummary(regionLabels),
            regions.map((region) => {
              const listed = selectedRegionIds.includes(region.id);
              const isActive = region.id === activeRegionId;
              const title = regionName(region.title);
              const tone = isActive
                ? 'active'
                : listed
                  ? 'listed'
                  : moodRegionIds.includes(region.id)
                    ? 'mood'
                    : undefined;
              return (
                <span
                  className={chipCss({ tone }, css.regionChip)}
                  key={region.id}
                >
                  <button
                    aria-current={isActive ? 'true' : undefined}
                    aria-pressed={listed}
                    className={css.chipPart}
                    onClick={() => filterBar.onSelectRegion(region.id)}
                    type="button"
                  >
                    {title}
                  </button>
                  {listed ? (
                    <button
                      aria-label={`Remove ${title}`}
                      className={css.chipRemove}
                      onClick={() => filterBar.onRemoveRegion(region.id)}
                      type="button"
                    >
                      ✕
                    </button>
                  ) : null}
                </span>
              );
            })
          )}

          <fieldset aria-label="Type" className={css.segmented}>
            {advisorProductTypes.map((type) => (
              <button
                aria-pressed={selectedType === type}
                className={segmentCss({ isActive: selectedType === type })}
                key={type}
                onClick={() => filterBar.onSelectType(type)}
                type="button"
              >
                {TYPE_LABELS[type]}
                {counts.byType[type] === null ? null : ' '}
                {counts.byType[type] === null ? null : (
                  <span className={css.segmentCount}>
                    {counts.byType[type]}
                  </span>
                )}
              </button>
            ))}
          </fieldset>

          {hotel
            ? group(
                'stars',
                'Stars',
                selectionSummary(starLabels),
                HOTEL_STAR_FILTERS.map((stars) => {
                  const selected = hotel.stars.includes(stars);
                  return (
                    <button
                      aria-label={`${stars} stars`}
                      aria-pressed={selected}
                      className={chipCss({
                        tone: selected ? 'active' : undefined,
                      })}
                      key={stars}
                      onClick={() => hotel.onToggleStars(stars)}
                      type="button"
                    >
                      {stars}★
                    </button>
                  );
                })
              )
            : null}

          {hotel
            ? group(
                'price',
                'Price per night',
                selectionSummary(priceLabels),
                HOTEL_PRICE_LEVELS.map((level) => {
                  const selected = hotel.priceLevels.includes(level);
                  return (
                    <button
                      aria-label={`${priceLevelSymbol(level)} ${priceLevelRange(level)}`}
                      aria-pressed={selected}
                      className={chipCss({
                        tone: selected ? 'active' : undefined,
                      })}
                      key={level}
                      onClick={() => hotel.onTogglePriceLevel(level)}
                      title={`Sold ${priceLevelRange(level)} a night on average`}
                      type="button"
                    >
                      {priceLevelSymbol(level)} · {priceLevelRange(level)}
                    </button>
                  );
                })
              )
            : null}

          {group(
            'mood',
            'Mood',
            selectionSummary(moodLabels),
            moods.map((mood) => {
              const selected = selectedMoods.includes(mood);
              return (
                <button
                  aria-pressed={selected}
                  className={chipCss({
                    tone: selected
                      ? 'active'
                      : isBriefMood(mood, briefMoods)
                        ? 'brief'
                        : undefined,
                  })}
                  key={mood}
                  onClick={() => filterBar.onSelectMood(mood)}
                  type="button"
                >
                  {titleCaseRegionTitle(mood)}
                </button>
              );
            }),
            briefMoods.length > 0 ? (
              <span className={css.fromBrief}>from client brief</span>
            ) : null
          )}

          <div className={css.footer}>
            <button
              className={css.textButton}
              onClick={() => setOpen(toggleAllGroups(open))}
              type="button"
            >
              {allOpen ? 'Collapse groups' : 'Expand groups'}
            </button>
            <button
              className={css.textButton}
              disabled={active === 0}
              onClick={() => {
                onSearchChange('');
                onClear();
              }}
              type="button"
            >
              Clear ({active})
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
